import { useState } from 'react';
import { Download, Eye, Loader2, Phone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useT } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import {
  downloadCallOutcomesCsv,
  previewCallOutcomes,
  type CallOutcomeExportRow,
  type CallOutcomePreviewResponse,
} from '@/features/admin/reports.api';

function isoDateNDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const da = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function todayIso(): string {
  return isoDateNDaysAgo(0);
}

/**
 * F3 — admin download of call outcomes (CORE / REACH / EXHAUSTED).
 * Default: last 7 days, all three presets on. last-4 only; no fee amounts.
 */
export function CallOutcomesExportSection(): JSX.Element {
  const t = useT();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [from, setFrom] = useState<string>(isoDateNDaysAgo(6));
  const [to, setTo] = useState<string>(todayIso());
  const [core, setCore] = useState(true);
  const [reach, setReach] = useState(true);
  const [exhausted, setExhausted] = useState(true);
  const [loading, setLoading] = useState<'preview' | 'download' | null>(null);
  const [preview, setPreview] = useState<CallOutcomePreviewResponse | null>(null);

  const filters = { from, to, core, reach, exhausted };

  const runPreview = async (): Promise<void> => {
    setLoading('preview');
    try {
      const data = await previewCallOutcomes(filters);
      setPreview(data);
    } catch (err) {
      toast.error(t('admin.reports.outcomesExport.error'), (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  const runDownload = async (): Promise<void> => {
    if (!accessToken) return;
    setLoading('download');
    try {
      await downloadCallOutcomesCsv(filters, accessToken);
    } catch (err) {
      toast.error(t('admin.reports.outcomesExport.error'), (err as Error).message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Card className="p-4 border-[#c9e2e2]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="font-head text-[13px] font-bold text-nav-navy flex items-center gap-2">
            <Phone className="h-4 w-4 text-nav-teal" />
            {t('admin.reports.outcomesExport.title')}
          </h2>
          <p className="text-xs text-nav-slate mt-1 max-w-2xl">
            {t('admin.reports.outcomesExport.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border border-nav-card rounded-md px-2 py-1 text-xs text-nav-navy"
          />
          <span className="text-nav-faint text-xs">→</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border border-nav-card rounded-md px-2 py-1 text-xs text-nav-navy"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void runPreview()}
            disabled={loading !== null}
          >
            {loading === 'preview' ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-1" />
            )}
            {t('admin.reports.outcomesExport.preview')}
          </Button>
          <Button
            size="sm"
            onClick={() => void runDownload()}
            disabled={!accessToken || loading !== null}
          >
            {loading === 'download' ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            {t('admin.reports.outcomesExport.download')}
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Checkbox
          checked={core}
          onChange={(e) => setCore(e.currentTarget.checked)}
          label={t('admin.reports.outcomesExport.core')}
          description={t('admin.reports.outcomesExport.coreHint')}
        />
        <Checkbox
          checked={reach}
          onChange={(e) => setReach(e.currentTarget.checked)}
          label={t('admin.reports.outcomesExport.reach')}
          description={t('admin.reports.outcomesExport.reachHint')}
        />
        <Checkbox
          checked={exhausted}
          onChange={(e) => setExhausted(e.currentTarget.checked)}
          label={t('admin.reports.outcomesExport.exhausted')}
          description={t('admin.reports.outcomesExport.exhaustedHint')}
        />
      </div>

      {preview && (
        <div className="mt-4">
          <p className="text-xs text-nav-slate mb-2">
            {t('admin.reports.outcomesExport.total', {
              total: preview.total,
              shown: preview.returned,
            })}
            {preview.truncated ? ` — ${t('admin.reports.outcomesExport.truncated')}` : ''}
          </p>
          {preview.rows.length === 0 ? (
            <p className="text-sm text-nav-slate">{t('admin.reports.outcomesExport.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-nav-card">
              <table className="min-w-full text-xs">
                <thead className="bg-nav-softer text-nav-slate">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.clientId')}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.outcome')}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.attemptAt')}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.agent')}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.assignmentId')}</th>
                    <th className="px-2 py-1.5 text-right font-medium">{t('admin.reports.outcomesExport.col.attemptCount')}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.phoneLast4')}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t('admin.reports.outcomesExport.col.clientStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row: CallOutcomeExportRow) => (
                    <tr key={`${row.assignment_id}-${row.attempt_at}-${row.outcome}`} className="border-t border-nav-hair">
                      <td className="px-2 py-1.5 font-mono">{row.client_id}</td>
                      <td className="px-2 py-1.5">{row.outcome}</td>
                      <td className="px-2 py-1.5 font-mono">{row.attempt_at}</td>
                      <td className="px-2 py-1.5">{row.agent_name || row.agent_id}</td>
                      <td className="px-2 py-1.5 font-mono">{row.assignment_id}</td>
                      <td className="px-2 py-1.5 text-right">{row.attempt_count}</td>
                      <td className="px-2 py-1.5 font-mono">{row.phone_last4}</td>
                      <td className="px-2 py-1.5">{row.client_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
